// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { OptionGroup } from "../OptionGroup"
import { RequiredMark } from "../RequiredMark"
import { Checkbox } from "../Checkbox"
import { Field } from "../Field"
import { Input } from "@/components/ui/input"
import { useI18n } from "@/i18n"
import type {
  ActivityLevel,
  ActivityPreviewInput,
  SportEntryInput,
  SportIntensity,
  SportType,
} from "../calorieTarget"

const SPORT_TYPES: SportType[] = [
  "strength",
  "running",
  "cycling",
  "swimming",
  "team_sports",
  "yoga_pilates",
  "walking_hiking",
  "other",
]

const ACTIVITY_LEVELS: ActivityLevel[] = ["sedentary", "light", "moderate", "high"]
const ACTIVITY_LEVEL_KEY: Record<ActivityLevel, "Sedentary" | "Light" | "Moderate" | "High"> = {
  sedentary: "Sedentary",
  light: "Light",
  moderate: "Moderate",
  high: "High",
}

const DEFAULT_SESSIONS_PER_WEEK = 2
const DEFAULT_INTENSITY: SportIntensity = "moderate"

interface Props {
  value: ActivityPreviewInput
  onChange: (next: ActivityPreviewInput) => void
}

/**
 * ADR-027 rule 7 activity/energy capture, rendered inside Step4Body (onboarding
 * step 4, "your starting point") ABOVE the target-calculation section, since
 * the calculated target depends on this input. Kept as its own file so the
 * concurrently-edited Step4Body.tsx only needs a small anchored insertion
 * point rather than a large diff.
 *
 *  - Base level: single-select, required by the UI (steps.ts's STEP_SCHEMAS[4]
 *    gate), four concrete daily-movement descriptions — deliberately NOT
 *    about exercise (that's the sports section below, summed separately so
 *    the two never double-count).
 *  - Sports: optional, structured — toggled type chips; each active type
 *    gets its own sessions/week + intensity + optional minutes row.
 *  - Wearable: optional average daily active-calories entry, which (per
 *    shared/targets.ts) REPLACES the level/sports estimate with a device-
 *    reported one when present.
 */
export function ActivitySection({ value, onChange }: Props) {
  const { t } = useI18n()

  function setLevel(level: ActivityLevel) {
    onChange({ ...value, level })
  }

  function toggleSport(type: SportType) {
    const exists = value.sports.some(s => s.type === type)
    const sports = exists
      ? value.sports.filter(s => s.type !== type)
      : [
          ...value.sports,
          {
            type,
            sessionsPerWeek: DEFAULT_SESSIONS_PER_WEEK,
            intensity: DEFAULT_INTENSITY,
            minutesPerSession: null,
          },
        ]
    onChange({ ...value, sports })
  }

  function updateSport(type: SportType, patch: Partial<SportEntryInput>) {
    onChange({
      ...value,
      sports: value.sports.map(s => (s.type === type ? { ...s, ...patch } : s)),
    })
  }

  function toggleWearable(enabled: boolean) {
    onChange({
      ...value,
      wearableKcalEnabled: enabled,
      avgDeviceActiveKcal: enabled ? value.avgDeviceActiveKcal : null,
    })
  }

  function setDeviceKcal(raw: string) {
    const num = raw === "" ? null : Number(raw)
    onChange({ ...value, avgDeviceActiveKcal: Number.isFinite(num) ? num : null })
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-medium text-ink">
            {t("onboarding.step4.activityLevelLabel")}
          </span>
          <RequiredMark />
        </div>
        <p className="text-xs text-ink-2">{t("onboarding.step4.activityLevelBody")}</p>
        <OptionGroup
          legend={t("onboarding.step4.activityLevelLabel")}
          columns={2}
          value={value.level}
          onChange={v => setLevel(v as ActivityLevel)}
          options={ACTIVITY_LEVELS.map(level => ({
            value: level,
            label: t(`onboarding.step4.activity${ACTIVITY_LEVEL_KEY[level]}`),
            sublabel: t(`onboarding.step4.activity${ACTIVITY_LEVEL_KEY[level]}Body`),
          }))}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">{t("onboarding.step4.sportsQuestion")}</span>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={t("onboarding.step4.sportsQuestion")}
        >
          {SPORT_TYPES.map(type => {
            const active = value.sports.some(s => s.type === type)
            return (
              <button
                key={type}
                type="button"
                aria-pressed={active}
                onClick={() => toggleSport(type)}
                className={`min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-green bg-green/10 text-ink"
                    : "border-line bg-paper text-ink hover:border-green/60"
                }`}
              >
                {t(`onboarding.step4.sportsTypes.${sportTypeKey(type)}`)}
              </button>
            )
          })}
        </div>

        {value.sports.length > 0 && (
          <div className="flex flex-col gap-3">
            {value.sports.map(sport => (
              <div
                key={sport.type}
                className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-ink">
                    {t(`onboarding.step4.sportsTypes.${sportTypeKey(sport.type)}`)}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleSport(sport.type)}
                    className="text-xs font-medium text-ink-2 underline underline-offset-2 hover:text-ink"
                  >
                    {t("onboarding.step4.sportsRemove")}
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-3 sm:items-end">
                  <Field
                    label={t("onboarding.step4.sportsSessionsLabel")}
                    htmlFor={`sport-sessions-${sport.type}`}
                    required={false}
                  >
                    <Input
                      id={`sport-sessions-${sport.type}`}
                      type="number"
                      min={1}
                      max={14}
                      value={sport.sessionsPerWeek}
                      onChange={e => {
                        const n = Number(e.target.value)
                        if (Number.isFinite(n))
                          updateSport(sport.type, {
                            sessionsPerWeek: Math.min(14, Math.max(1, Math.round(n))),
                          })
                      }}
                    />
                  </Field>
                  <Field
                    label={t("onboarding.step4.sportsMinutesLabel")}
                    htmlFor={`sport-minutes-${sport.type}`}
                    required={false}
                  >
                    <Input
                      id={`sport-minutes-${sport.type}`}
                      type="number"
                      min={5}
                      max={240}
                      placeholder={t("onboarding.step4.sportsMinutesPlaceholder")}
                      value={sport.minutesPerSession ?? ""}
                      onChange={e => {
                        const raw = e.target.value
                        const n = raw === "" ? null : Number(raw)
                        updateSport(sport.type, {
                          minutesPerSession: n !== null && Number.isFinite(n) ? n : null,
                        })
                      }}
                    />
                  </Field>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-ink">
                      {t("onboarding.step4.sportsIntensityLabel")}
                    </span>
                    <div className="flex gap-1.5">
                      {(["low", "moderate", "high"] as SportIntensity[]).map(intensity => (
                        <button
                          key={intensity}
                          type="button"
                          aria-pressed={sport.intensity === intensity}
                          onClick={() => updateSport(sport.type, { intensity })}
                          className={`min-h-9 flex-1 rounded-bb-md border px-2 py-1 text-xs font-medium transition-colors ${
                            sport.intensity === intensity
                              ? "border-green bg-green/10 text-ink"
                              : "border-line bg-paper text-ink hover:border-green/60"
                          }`}
                        >
                          {t(`onboarding.step4.sportsIntensity.${intensity}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-3">
        <label className="flex items-start gap-2 text-sm text-ink">
          <Checkbox
            id="ob-wearable-enabled"
            checked={value.wearableKcalEnabled}
            onCheckedChange={toggleWearable}
          />
          <span>{t("onboarding.step4.wearableQuestion")}</span>
        </label>
        {value.wearableKcalEnabled && (
          <Field
            label={t("onboarding.step4.wearableLabel")}
            htmlFor="ob-wearable-kcal"
            required={false}
          >
            <Input
              id="ob-wearable-kcal"
              type="number"
              min={0}
              max={3000}
              value={value.avgDeviceActiveKcal ?? ""}
              onChange={e => setDeviceKcal(e.target.value)}
            />
          </Field>
        )}
        {value.wearableKcalEnabled && (
          <p className="text-xs text-ink-2">{t("onboarding.step4.wearableHint")}</p>
        )}
      </div>
    </div>
  )
}

function sportTypeKey(
  type: SportType
):
  | "strength"
  | "running"
  | "cycling"
  | "swimming"
  | "teamSports"
  | "yogaPilates"
  | "walkingHiking"
  | "other" {
  switch (type) {
    case "team_sports":
      return "teamSports"
    case "yoga_pilates":
      return "yogaPilates"
    case "walking_hiking":
      return "walkingHiking"
    default:
      return type
  }
}
